import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const MaintenanceCycleLimitsManagement = () => {
  const [limits, setLimits] = useState([]);
  const [repairProcessTypes, setRepairProcessTypes] = useState([]);
  const [corrosionCategories, setCorrosionCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLimit, setEditingLimit] = useState(null);
  const [formData, setFormData] = useState({
    repairProcessTypeId: '',
    corrosionCategoryId: '',
    maxApplications: '',
    description: '',
    isActive: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [processTypeFilter, setProcessTypeFilter] = useState('');

  useEffect(() => {
    fetchLimits();
    fetchRepairProcessTypes();
    fetchCorrosionCategories();
  }, []);

  const fetchLimits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/maintenance-cycle-limits');
      if (response.data.success) {
        setLimits(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch cycle limits');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairProcessTypes = async () => {
    try {
      const response = await api.get('/repair-process-types?isActive=true');
      if (response.data.success) {
        setRepairProcessTypes(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching repair process types:', err);
    }
  };

  const fetchCorrosionCategories = async () => {
    try {
      const response = await api.get('/corrosion-categories?isActive=true');
      if (response.data.success) {
        setCorrosionCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching corrosion categories:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLimit) {
        await api.put(`/maintenance-cycle-limits/${editingLimit.id}`, formData);
      } else {
        await api.post('/maintenance-cycle-limits', formData);
      }
      await fetchLimits();
      setShowModal(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save cycle limit');
    }
  };

  const handleEdit = (limit) => {
    setEditingLimit(limit);
    setFormData({
      repairProcessTypeId: limit.repairProcessTypeId,
      corrosionCategoryId: limit.corrosionCategoryId || '',
      maxApplications: limit.maxApplications || '',
      description: limit.description || '',
      isActive: limit.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (limit) => {
    if (!window.confirm(`Are you sure you want to delete this cycle limit?`)) {
      return;
    }
    try {
      await api.delete(`/maintenance-cycle-limits/${limit.id}`);
      await fetchLimits();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete cycle limit');
    }
  };

  const resetForm = () => {
    setFormData({
      repairProcessTypeId: '',
      corrosionCategoryId: '',
      maxApplications: '',
      description: '',
      isActive: true
    });
    setEditingLimit(null);
  };

  const filteredLimits = limits.filter(limit => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        limit.repairProcessType?.processName?.toLowerCase().includes(searchLower) ||
        limit.corrosionCategory?.categoryCode?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (processTypeFilter) {
      if (limit.repairProcessTypeId.toString() !== processTypeFilter) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Maintenance Cycle Limits</h1>
        <p className="text-gray-600">Configure minimum/maximum cycle limits and maximum applications for repair processes</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by process or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={processTypeFilter}
          onChange={(e) => setProcessTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Process Types</option>
          {repairProcessTypes.map(type => (
            <option key={type.id} value={type.id}>{type.processName}</option>
          ))}
        </select>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
        >
          <FontAwesomeIcon icon={faPlus} className="-ml-0.5 mr-1.5 h-5 w-5" />
          Add Limit
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Repair Process</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Corrosion Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Applications</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLimits.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No cycle limits found
                </td>
              </tr>
            ) : (
              filteredLimits.map(limit => (
                <tr key={limit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{limit.repairProcessType?.processName || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {limit.corrosionCategory ? `${limit.corrosionCategory.categoryCode}` : 'All Categories'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{limit.maxApplications || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      limit.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {limit.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(limit)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <button
                      onClick={() => handleDelete(limit)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingLimit ? 'Edit' : 'Add'} Maintenance Cycle Limit
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repair Process Type *
                  </label>
                  <select
                    name="repairProcessTypeId"
                    value={formData.repairProcessTypeId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Process Type</option>
                    {repairProcessTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.processName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corrosion Category (Optional)
                  </label>
                  <select
                    name="corrosionCategoryId"
                    value={formData.corrosionCategoryId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Categories</option>
                    {corrosionCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.categoryCode} - {cat.categoryName}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to apply to all categories
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Applications *
                  </label>
                  <input
                    type="number"
                    name="maxApplications"
                    value={formData.maxApplications}
                    onChange={handleInputChange}
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 3"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum number of consecutive times this repair can be applied (e.g., 2-3 times)
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional description"
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
                  >
                    {editingLimit ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceCycleLimitsManagement;

